import { useState, useEffect } from "react";
import axios from "axios";
import { Car, ChevronDown, X } from "lucide-react";
import { API } from "./shopData";
import { formatCarModel } from "./formatCarModel";

export default function CarFilter({ brand, productCategory, onFilter, activeCarMake, activeCarModel }) {
  const [makes, setMakes] = useState([]);
  const [models, setModels] = useState([]);
  const [selectedMake, setSelectedMake] = useState(activeCarMake || "");
  const [selectedModel, setSelectedModel] = useState(activeCarModel || "");
  const [loading, setLoading] = useState(false);
  const contextLabel = brand || productCategory || "";

  useEffect(() => {
    setSelectedMake(activeCarMake || "");
    setSelectedModel(activeCarModel || "");
  }, [activeCarMake, activeCarModel, brand, productCategory]);

  useEffect(() => {
    if (!brand && !productCategory) { setMakes([]); return; }
    const params = {};
    if (brand) params.brand = brand;
    if (productCategory) params.product_category = productCategory;
    axios.get(`${API}/products/car-makes-for-brand`, { params })
      .then((r) => setMakes(r.data))
      .catch(() => {});
  }, [brand, productCategory]);

  useEffect(() => {
    if (!selectedMake) { setModels([]); return; }
    setLoading(true);
    const params = { make: selectedMake };
    if (brand) params.brand = brand;
    if (productCategory) params.product_category = productCategory;
    axios.get(`${API}/products/car-models`, { params })
      .then((r) => setModels(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [selectedMake, brand, productCategory]);

  const handleMakeChange = (make) => {
    setSelectedMake(make);
    setSelectedModel("");
    if (make) {
      onFilter(make, null);
    } else {
      onFilter(null, null);
    }
  };

  const handleModelChange = (model) => {
    setSelectedModel(model);
    onFilter(selectedMake, model || null);
  };

  const clearFilter = () => {
    setSelectedMake("");
    setSelectedModel("");
    onFilter(null, null);
  };

  if (makes.length === 0) return null;

  const selectCls = "w-full bg-[#0A0A0A] border border-[#1E1E1E] rounded-lg px-3 py-2.5 text-sm text-white appearance-none focus:border-[#FF3B30]/50 focus:outline-none transition-colors cursor-pointer";

  return (
    <div data-testid="car-filter" className="bg-[#111] border border-[#1E1E1E] rounded-xl p-4 mb-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Car size={15} className="text-[#FF3B30]" />
          <span className="text-white text-xs font-bold uppercase tracking-wider">Zoek voor jouw auto</span>
        </div>
        {selectedMake && (
          <button onClick={clearFilter} className="flex items-center gap-1 text-[#666] hover:text-white text-xs transition-colors" data-testid="car-filter-clear">
            <X size={12} /> Wissen
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {/* Car Make */}
        <div className="relative">
          <select
            value={selectedMake}
            onChange={(e) => handleMakeChange(e.target.value)}
            className={selectCls}
            data-testid="car-filter-make"
          >
            <option value="">Kies auto merk...</option>
            {makes.map((m) => (
              <option key={m.make} value={m.make}>{m.make} ({m.count})</option>
            ))}
          </select>
          <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#555] pointer-events-none" />
        </div>

        {/* Car Model */}
        <div className="relative">
          <select
            value={selectedModel}
            onChange={(e) => handleModelChange(e.target.value)}
            disabled={!selectedMake || loading}
            className={`${selectCls} ${!selectedMake ? "opacity-40" : ""}`}
            data-testid="car-filter-model"
          >
            <option value="">{loading ? "Laden..." : selectedMake ? "Kies model..." : "Eerst merk kiezen"}</option>
            {models.map((m) => {
              const { display, details } = formatCarModel(m.model, selectedMake);
              const label = details ? `${display} · ${details}` : display;
              return (
                <option key={m.model} value={m.model}>{`${label} (${m.count})`}</option>
              );
            })}
          </select>
          <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#555] pointer-events-none" />
        </div>
      </div>

      {selectedMake && (
        <p className="text-[#555] text-[10px] mt-2">
          {selectedModel
            ? `Toont ${contextLabel} producten voor ${selectedMake} ${selectedModel}`
            : `Toont alle ${contextLabel} producten voor ${selectedMake}`}
        </p>
      )}
    </div>
  );
}
